/**
 * Utility functions for determining file types and icons
 */

export const getFileExtension = (filename: string): string => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};
  
/**
 * Normalize a file type (MIME type or extension) to just the extension
 * Examples:
 * - "text/html" -> "html"
 * - "text/markdown" -> "md"
 * - "html" -> "html"
 * - "application/pdf" -> "pdf"
 */
export const normalizeFileType = (fileType: string): string => {
// If it looks like a MIME type (contains '/'), extract the subtype
if (fileType.includes('/')) {
    const mimeTypeMap: Record<string, string> = {
    'text/html': 'html',
    'text/markdown': 'md',
    'text/plain': 'txt',
    'application/pdf': 'pdf',
    'application/json': 'json',
    'application/xml': 'xml',
    'text/xml': 'xml',
    'text/css': 'css',
    'text/javascript': 'js',
    'application/javascript': 'js',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/svg+xml': 'svg',
    'image/webp': 'webp',
    };
    
    // Check if we have a direct mapping
    if (mimeTypeMap[fileType]) {
    return mimeTypeMap[fileType];
    }
    
    // Otherwise, try to extract the subtype
    const parts = fileType.split('/');
    return parts[1]?.toLowerCase() || fileType;
}

// Already an extension
return fileType.toLowerCase();
};
  
export const getFileTypeCategory = (extension: string): string => {
  // Normalize the extension first (in case it's a MIME type)
  const normalizedExt = normalizeFileType(extension);
  
  const wordExtensions = ['doc', 'docx'];
  const excelExtensions = ['xls', 'xlsx', 'csv'];
  const powerpointExtensions = ['ppt', 'pptx'];
  const pdfExtensions = ['pdf'];
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'];
  const codeExtensions = ['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'cpp', 'c', 'cs', 'rb', 'go', 'rs', 'php', 'html', 'css', 'scss', 'json', 'xml', 'yaml', 'yml', 'md', 'txt'];

  if (wordExtensions.includes(normalizedExt)) return 'word';
  if (excelExtensions.includes(normalizedExt)) return 'excel';
  if (powerpointExtensions.includes(normalizedExt)) return 'powerpoint';
  if (pdfExtensions.includes(normalizedExt)) return 'pdf';
  if (imageExtensions.includes(normalizedExt)) return 'image';
  if (codeExtensions.includes(normalizedExt)) return 'code';
  
  return 'other';
};
  
export const getFileIcon = (extension: string): string => {
  const category = getFileTypeCategory(extension);
  
  // Map to icon file names (these should match your icon assets)
  const iconMap: Record<string, string> = {
    'word': '📄', // Word icon
    'excel': '📊', // Excel icon
    'powerpoint': '📽️', // PowerPoint icon
    'pdf': '📕', // PDF icon
    'image': '🖼️', // Image icon
    'code': '💻', // Code icon
    'other': '📋', // Generic file icon
  };
  
  return iconMap[category] || iconMap['other'];
};
  
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};